import { useCurrentOrg } from 'common/hooks/useCurrentOrg'
import { useOrgBillingDetails } from 'common/hooks/useOrgBillingDetails'
import { useOrgSubscription } from 'common/hooks/useOrgSubscription'
import { createContext, useEffect, useReducer } from 'react'
import { useLocation } from 'react-router-dom'
import { coreApiClient } from 'common/api'
import { generateReservedCredits } from '../utils/reservedCreditsData'
import {
  getLegacySubscriptionBundles,
  getSubscriptionDetails,
  getSubscriptionType,
  getProduct,
  isFreeSubscription,
  isLegacySubscription,
  productsIds,
  getStateSubscriptionDetails,
} from '../utils/enrichSubscriptionUtils'

export const BillingContext = createContext()

/**
 * Initial state for the billing context
 */
const initialState = {
  // Checkboxes
  isSmallBusiness: false,
  isPayAsYouGo: true, // This uses the overage product
  isReserved: false,
  isQualified: true,
  isPremiumSupport: false,
  legacyReservedCredits: { 15: 0, 50: 0, 300: 0 }, //Selected credits count
  recurring: {
    term: 12,
    interval: 1,
  },
  reservedCredits: generateReservedCredits()?.[0]?.units, // Reserved credits used for reserved credits slider
  isCancelDialogOpen: false, // Cancel plan dialog
}
/**
 * Actions used by the reducer
 */
export const actions = {
  setLegacyReservedCredits: 'SET_LEGACY_RESERVED_CREDITS',
  toggleIsSmallBusiness: 'TOGGLE_IS_SMALL_BUSINESS',
  toggleIsQualified: 'TOGGLE_IS_QUALIFIED',
  toggleIsReserved: 'TOGGLE_IS_RESERVED',
  toggleIsPayAsYouGo: 'TOGGLE_PAY_AS_YOU_GO',
  toggleIsCancelDialogOpen: 'TOGGLE_IS_CANCEL_DIALOG_OPEN',
  toggleIsPremiumSupport: 'TOGGLE_IS_PREMIUM_SUPPORT',
  setReservedCredits: 'SET_RESERVED_CREDITS',
  setRecurring: 'SET_RECURRING',
}
/**
 *
 * @param {object} state - current state object
 * @param {object} action - action object with type and payload
 * @returns
 */
function reducer(state, action) {
  switch (action.type) {
    case actions.setLegacyReservedCredits:
      return {
        ...state,
        legacyReservedCredits: { ...state.legacyReservedCredits, ...action.payload },
      }
    case actions.toggleIsSmallBusiness:
      return { ...state, isSmallBusiness: action.payload }
    case actions.toggleIsQualified:
      return { ...state, isQualified: action.payload }
    case actions.toggleIsCancelDialogOpen:
      return { ...state, isCancelDialogOpen: action.payload }
    case actions.toggleIsPayAsYouGo:
      return { ...state, isPayAsYouGo: action.payload }
    case actions.toggleIsPremiumSupport:
      return { ...state, isPremiumSupport: action.payload }
    case actions.toggleIsReserved:
      return { ...state, isReserved: action.payload }
    case actions.setReservedCredits:
      return { ...state, reservedCredits: action.payload }
    case actions.setRecurring:
      return { ...state, recurring: action.payload }
    default:
      return state
  }
}

export const BillingProvider = ({ children }) => {
  /**
   * Get current org id
   */
  const {
    org: { orgUid: orgId },
  } = useCurrentOrg()
  const { pathname } = useLocation()

  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    orgSubscription: subscription,
    orgSubscriptionError,
    refreshSubscription,
  } = useOrgSubscription({
    orgId,
  })
  const { mutate: refreshBillingDetails } = useOrgBillingDetails()

  const isLoadingSubscription = !subscription && !orgSubscriptionError

  /**
   * Set initial state based on current subscription
   */
  useEffect(() => {
    /**
     * Set initial state values based on subscription
     * If subscription data is not available or is free, use the initial state
     */
    const useSubscriptionData = !isFreeSubscription(subscription)
    /**
     * If has subscription data, update initial states
     */
    if (useSubscriptionData) {
      /**
       * Set legacy subscription reserved credits bundles units
       */
      dispatch({
        type: actions.setLegacyReservedCredits,
        payload: getLegacySubscriptionBundles(subscription),
      })

      dispatch({
        type: actions.setReservedCredits,
        payload: getSubscriptionDetails(subscription)?.totalCredits || initialState.reservedCredits,
      })

      /**
       * Set initial 'isSmallBusiness' based on current subscription
       */
      dispatch({
        type: actions.toggleIsSmallBusiness,
        payload: getSubscriptionType(subscription)?.isSmallBusiness,
      })

      /**
       * Set initial 'isQualified' based on current subscription
       */

      dispatch({
        type: actions.toggleIsQualified,
        payload: subscription?.isQualified,
      })

      /**
       * Set initial 'recurring' based on current subscription start, end dates, and interval
       */

      dispatch({
        type: actions.setRecurring,
        payload: getSubscriptionDetails(subscription)?.recurring,
      })

      /**
       * Set initial 'isReserved' based on current subscription
       */
      dispatch({
        type: actions.toggleIsReserved,
        payload: getSubscriptionType(subscription)?.isReserved,
      })

      /**
       * Set initial 'isPayAsYouGo' based on current subscription
       */
      dispatch({
        type: actions.toggleIsPayAsYouGo,
        payload: getSubscriptionType(subscription)?.isPayAsYouGo,
      })

      dispatch({
        type: actions.toggleIsPremiumSupport,
        payload: !!getProduct(subscription, productsIds?.premiumSupport),
      })
    }
  }, [subscription, pathname])

  /**
   *
   * @param {String} plan - Can be 15, 50, 300, or 'all'. If 'all' then set all plans to the passed 'units'
   * @param {Number} units - Number of units to set for the plan
   */
  const updateLegacyReservedCreditsCount = (unitSize, units) => {
    if (unitSize === 'all') {
      dispatch({
        type: actions.setLegacyReservedCredits,
        payload: initialState.legacyReservedCredits,
      })
    } else {
      dispatch({ type: actions.setLegacyReservedCredits, payload: { [unitSize]: units } })
    }
  }

  const isLoading = isLoadingSubscription

  /**
   * Refresh the subscription and billing details
   */
  const refresh = () => {
    refreshSubscription()
    refreshBillingDetails()
  }

  /**
   *
   * @param {String} Provider - provider type string (aws, stripe, manual)
   * @param {String} providerCustomerAccountId - AWS account id if provider is AWS
   * @returns
   */
  const subscribe = async ({ provider, providerCustomerAccountId } = {}) => {
    const data = {}
    const stateSubscription = getStateSubscriptionDetails(state, subscription)
    let products = []
    if (isLegacySubscription(subscription)) {
      products = stateSubscription?.products?.map((product) => ({
        id: product.id,
        units: product.units,
      }))
      if (state?.isPayAsYouGo) {
        products.push({
          id: 'credits1_overage_v1',
          units: 1,
        })
      }
    } else {
      products = stateSubscription?.products?.map(
        ({ id, endDate, startDate, units, autoRenew, interval, unitSize }) => ({
          id,
          endDate,
          startDate,
          units,
          autoRenew,
          interval,
          unitSize,
        })
      )
    }
    data.products = products
    data.isSmallBusiness = state.isSmallBusiness
    data.isQualified = state.isQualified

    if (provider) {
      data.provider = provider
    }
    if (providerCustomerAccountId) {
      data.providerCustomerAccountId = providerCustomerAccountId
    }

    let obj = {
      url: `/billing/orgs/${orgId}/subscribe`,
      method: 'post',
      data,
    }

    return await coreApiClient(obj)
  }

  /**
   * Reset all plans credits to 0
   */
  const resetCredits = () => {
    updateLegacyReservedCreditsCount('all', 0)
    dispatch({
      type: actions.setReservedCredits,
      payload: initialState.reservedCredits,
    })
  }

  /**
   *
   * @param {Boolean} payload - true or false
   * @param {Boolean} reservedPayload - true or false
   * When user toggles the pay as you go switch, we need to set the reserved switch as well
   *
   */
  const toggleIsPayAsYouGo = ({ payload, reservedPayload = false }) => {
    dispatch({
      type: actions.toggleIsPayAsYouGo,
      payload,
    })

    dispatch({
      type: actions.toggleIsReserved,
      payload: reservedPayload,
    })
    if (!reservedPayload) {
      dispatch({
        type: actions.setRecurring,
        payload: {
          term: 1,
          interval: 1,
        },
      })
    }
  }

  return (
    <BillingContext.Provider
      value={{
        subscription,
        state,
        isLoading,
        toggleIsPayAsYouGo,
        dispatch,
        refresh,
        subscribe,
        updateLegacyReservedCreditsCount,
        resetCredits,
      }}
    >
      {children}
    </BillingContext.Provider>
  )
}
